import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
// The site's stylesheet, here rather than in NBADesk now that NBADesk is
// its own chunk: every desk needs it.
import "./styles/index.css";
import { AccountProvider } from "./account/account";
// Side effect: starts listening for the Android install event before the first paint (DSPRT-185).
import "./shell/install";
import { installHeroTracking, installReveals, installSheetDrag, installSliders, installSortGlide } from "./shell/motion";
import { installPrefetch } from "./api/useNbaData";
import { ErrorBoundary, PageCrash } from "./shell/ErrorBoundary";
import { useDisplayKey } from "./shell/tz";
import { registerOfflineWorker } from "./shell/offline";
import { installVisitCount } from "./shell/visits";
import { deskOf } from "./shell/desks";

/**
 * Which desk to mount, by path. The UFC desk and the account pages (sign-in,
 * password reset, preferences, the admin tabs) are their own chunks, so an NBA page never downloads
 * them. Moving between desks is a full page load (the links between them are
 * plain hrefs), so this is read once.
 */
const UfcDesk = lazy(() => import("./ufc/UfcDesk"));
// The EuroLeague desk (DSPRT-451), its own chunk like the UFC's.
const EuroDesk = lazy(() => import("./euroleague/EuroDesk"));
// Its own chunk too (DSPRT-232): a UFC page loaded all 200 KB of it, the
// NBA desk being the one desk the entry imported directly.
const NBADesk = lazy(() => import("./NBADesk"));
const AccountDesk = lazy(() => import("./account/AccountDesk"));
// The fans' pages (DSPRT-433): Play and its games, friends, groups, profiles.
const FansDesk = lazy(() => import("./fans/FansDesk"));
// The path→desk rule is shared with search's openPage (src/shell/desks.ts).
const desk = deskOf(window.location.pathname);
const isUfc = desk === "ufc";
const isEuro = desk === "euro";
const isFans = desk === "fans";

// The tab's title from the address, before anything renders (DSPRT-212):
// index.html's own says "NBA Scores", and every page — a UFC one included —
// showed that until its view set the real one a moment later.
{
  const seg = window.location.pathname.split("/").filter(Boolean)[0] ?? "";
  const names: Record<string, string> = {
    ufc: "UFC", euroleague: "EuroLeague", gleague: "G League", scores: "NBA Scores", standings: "Standings", leaders: "League Leaders", playoffs: "Playoffs",
    alltime: "All-Time", wire: "The Wire", draft: "Draft", trade: "NBA Trade Machine", compare: "Compare",
    team: "Team", game: "Box score", player: "Player", about: "About", privacy: "Privacy", whatsnew: "What's new",
    play: "Play", friends: "Friends and groups", groups: "Group", join: "Join a group", invite: "You're invited", fan: "Fan", rivals: "Rivals",
  };
  document.title = seg === "" ? "StatSummit — NBA scores, standings and stats"
    : names[seg] ? `${names[seg]} · StatSummit` : "StatSummit";
}
const isAccount = desk === "account";

/**
 * Remounts the desk when a display choice changes: the time zone (DSPRT-299),
 * the 12- or 24-hour clock, feet or centimetres (DSPRT-303). A game's time is
 * worked out once, as its data arrives, so a fresh read is what makes every
 * time and measure on the page agree with the new choice.
 */
function ZoneKeyed({ children }: { children: React.ReactNode }) {
  return <React.Fragment key={useDisplayKey()}>{children}</React.Fragment>;
}

const container = document.getElementById("root");
if (!container) {
  // Fails loudly at load time rather than silently rendering nothing — an
  // index.html missing <div id="root"> is a build/deploy mistake, not a
  // runtime state worth degrading gracefully for.
  throw new Error("Root element #root not found in index.html");
}

// Tabs, toggles and nav links slide their selection instead of jumping (DSPRT-201).
installSliders();
// A profile opened from a headshot or crest flies that image into its header.
installHeroTracking();
// Long pages reveal as you scroll; the summit flag, banners and belts play once (DSPRT-201).
installReveals();
// DSPRT-611: sorted tables glide, a phone's sheet drags down to close, and a
// name, game or club starts loading while the pointer rests on it.
installSortGlide();
installSheetDrag();
installPrefetch();
// The service worker, for alerts and the offline page (DSPRT-294).
registerOfflineWorker();
// One count per page shown, for the admin Visitors page; no cookie (DSPRT-400).
installVisitCount();

ReactDOM.createRoot(container).render(
  <React.StrictMode>
    {/* A render error anywhere says so, with a way back, instead of leaving a blank page (DSPRT-291). */}
    <ErrorBoundary fallback={() => <PageCrash />}>
      <AccountProvider>
        <ZoneKeyed>
        {isUfc || isEuro || isAccount || isFans ? (
          <Suspense fallback={null}>
            {isUfc ? <UfcDesk /> : isEuro ? <EuroDesk /> : isFans ? <FansDesk /> : <AccountDesk />}
          </Suspense>
        ) : (
          <Suspense fallback={null}>
            <NBADesk />
          </Suspense>
        )}
        </ZoneKeyed>
      </AccountProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
